<template>
  <div>
    <v-modal x-short @close="$emit('close')">
      <template slot="#message-actions">
        <action-header />
        <action-body>
          <v-button
            v-if="isImage"
            block
            secondary
            ghost
            @click="handleView"
            class="action__button"
          >
            <v-icon icon="aspect_ratio"></v-icon>
            View Image
          </v-button>
          <v-button
            block
            secondary
            ghost
            @click="handleReply"
            class="action__button"
          >
            <v-icon icon="reply"></v-icon>
            Reply to {{ type }}
          </v-button>
          <v-button
            block
            secondary
            ghost
            @click="handleReact"
            class="action__button action__button--like"
          >
            <v-icon :class="[{ '--liked': isLiked }]" icon="favorite"></v-icon>
            {{ likeText }}
          </v-button>
          <v-button
            v-if="canDelete"
            block
            secondary
            ghost
            @click="() => (showDeleteConfirm = true)"
            class="action__button"
          >
            <v-icon icon="delete"></v-icon>
            Delete {{ type }}
          </v-button>
        </action-body>

        <action-footer row>
          <div class="d-flex align-items-center">
            <subscriber-avatar :subscriber="message.user" small class="mr-1" />
            <div>
              <div class="post-by font-weight-bold mb-0">
                Posted by {{ postByName }}
              </div>
              <small class="timestamp">{{ timeAgo(message.message_ts) }}</small>
            </div>
          </div>
        </action-footer>
      </template>
    </v-modal>

    <alert-dialog
      title="Are you sure you?"
      :actions="[
        {
          title: 'Cancel',
          callback: () => (showDeleteConfirm = false)
        },
        {
          title: 'Yes, delete',
          callback: handleDelete
        }
      ]"
      :show="showDeleteConfirm"
      @close="() => (showDeleteConfirm = false)"
    >
      This action is irreversible, the {{ type }} will no longer appear for you
      or anyone else.
    </alert-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

import { MESSAGE_TYPES, REACTION_TYPES } from "@/lib/pusher-utils"
import { timeAgo } from "@/lib/date-utils"
import { isMessageType } from "@/lib/messages-utils"

import ActionHeader from "@/components/modals/shared/ActionHeader"
import ActionBody from "@/components/modals/shared/ActionBody"
import ActionFooter from "@/components/modals/shared/ActionFooter"
import SubscriberAvatar from "@/components/SubscriberAvatar"

export default {
  components: {
    ActionHeader,
    ActionBody,
    ActionFooter,
    SubscriberAvatar,
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    isComment: { type: Boolean, default: false },
    isFeed: { type: Boolean, default: false },
    message: { type: Object, required: true },
    isLiked: { type: Boolean, default: false },
    username: { type: String, default: null }
  },

  data() {
    return {
      showDeleteConfirm: false,
      REACTION_TYPES
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),

    postByName() {
      return this.isSelf ? "you" : this.username
    },

    type() {
      return this.isComment ? "Comment" : this.isFeed ? "Post" : "Message"
    },

    isImage() {
      return this.message.message_type === MESSAGE_TYPES.IMAGE
    },

    isSelf() {
      return (
        isMessageType(this.message) &&
        this.message.user.id === this.currentUser?.id
      )
    },

    likeText() {
      return this.isLiked ? `Unlike ${this.type}` : `Like ${this.type}`
    },

    canDelete() {
      return (
        this.isAuthenticated &&
        (this.currentUser?.isAccountAdmin ||
          this.currentUser?.id === this.message.user.id)
      )
    }
  },

  methods: {
    timeAgo,

    handleView() {
      this.$emit("view")
      this.$router.removeHash()
    },

    handleReply() {
      if (!this.isAuthenticated) {
        this.$router.push("#auth")
      } else {
        this.$emit("reply")
        this.$router.removeHash()
      }
    },

    handleReact() {
      if (!this.isAuthenticated) {
        this.$router.push("#auth")
      } else {
        this.$emit(
          "react",
          this.isLiked ? REACTION_TYPES.UNLIKE : REACTION_TYPES.LIKE
        )
        this.$router.removeHash()
      }
    },

    handleDelete() {
      if (this.canDelete) {
        this.showDeleteConfirm = false
        this.$emit("delete", {
          user: this.currentUser,
          messageTs: this.message.message_ts,
          messageType: MESSAGE_TYPES.MESSAGE_DELETED,
          groupTs: this.message.group_ts
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.action__container {
  min-height: 40vh;
}

.action__button {
  &--like .--liked {
    color: $color-ui--red !important;
  }

  .v-icon {
    margin-right: 0.5rem;
  }
}

.post-by {
  color: $color-ui--grey-80;
  font-size: 16px;
}

.timestamp {
  color: $color-ui--grey-50;
  font-weight: 500;
}
</style>
